/**
 * Group used citations by source document for the source rails under chat
 * messages. Presentation-only: the backend still labels and cites per chunk;
 * this arranges the N chunk chips of one document inside a single chip so a
 * document cited on four pages reads as ONE source with four page pills, not
 * four indistinguishable truncated chips.
 */
export interface GroupedCitationChunk {
  origIndex: number
  displayIndex: number
}

export interface GroupedCitationDoc<S> {
  documentId: string
  documentName: string
  firstSource: S
  chunks: GroupedCitationChunk[]
}

export function groupCitationsByDocument<
  S extends { documentId: string; documentName: string },
>(
  usedCitations: number[],
  origToDisplay: Map<number, number>,
  sources: readonly S[],
): GroupedCitationDoc<S>[] {
  const byDoc = new Map<string, GroupedCitationDoc<S>>()
  const ordered: GroupedCitationDoc<S>[] = []

  for (const origIndex of usedCitations) {
    const displayIndex = origToDisplay.get(origIndex)
    if (displayIndex === undefined) continue
    const source = sources[origIndex - 1]
    if (!source) continue

    let group = byDoc.get(source.documentId)
    if (!group) {
      group = {
        documentId: source.documentId,
        documentName: source.documentName,
        firstSource: source,
        chunks: [],
      }
      byDoc.set(source.documentId, group)
      ordered.push(group)
    }
    group.chunks.push({ origIndex, displayIndex })
  }

  return ordered
}
